import React, { useState, useEffect, useCallback, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem, Chip,
} from '@mui/material';
import {
  DndContext, DragOverlay, closestCorners, PointerSensor, useSensor, useSensors,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import { tasksAPI, sprintsAPI, searchAPI } from '../../services/api';
import KanbanColumn from '../KanbanColumn';
import TaskCard from '../TaskCard';
import TaskEditDialog from '../TaskEditDialog';
import BacklogTable from '../BacklogTable';
import BoardSearchBar from '../search/BoardSearchBar';
import SearchResultsPanel from '../search/SearchResultsPanel';

/**
 * Reusable Kanban board for a single project. Extracted from BoardPage so it can be
 * embedded in the PM workspace (internal + external). Backend scopes all data and
 * the move endpoint to the current user's customer.
 *
 * Props:
 *  - projectId (required)
 *  - sprintId (optional)  -> filters board by sprint
 *  - onTasksChanged (optional) -> called after any board mutation
 *
 * Exposes an imperative `reload()` via ref.
 */
const ProjectBoard = forwardRef(({ projectId, sprintId, onTasksChanged }, ref) => {
  const [boardData, setBoardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Sprint switcher: pick one of the (parallel) active sprints to focus, or "all".
  const [sprints, setSprints] = useState([]);
  const [selectedSprintId, setSelectedSprintId] = useState('all'); // 'all' | <sprintId>
  const [showAllDone, setShowAllDone] = useState(false);

  // Suche: filtert das Board in-place, findet aber mehr als das Board zeigt.
  const [searchQuery, setSearchQuery] = useState('');
  const [searchAllProjects, setSearchAllProjects] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  // Hochzählen erzwingt eine erneute Suche, ohne die Eingabe anzufassen –
  // nötig, wenn ein Treffer bearbeitet wurde und das Panel sonst veraltet.
  const [searchNonce, setSearchNonce] = useState(0);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  // Load all active sprints of this project (they run in parallel, no time-box).
  useEffect(() => {
    let cancelled = false;
    if (!projectId) return undefined;
    sprintsAPI.getAll(projectId, 'active')
      .then((res) => { if (!cancelled) setSprints(res.data || []); })
      .catch(() => { if (!cancelled) setSprints([]); });
    return () => { cancelled = true; };
  }, [projectId]);

  // The sprint the board is filtered by: explicit prop wins, else the picked sprint.
  const effectiveSprintId =
    (sprintId && sprintId !== 'all') ? sprintId
      : (selectedSprintId !== 'all' ? selectedSprintId : null);

  // Guard against out-of-order responses: the initial unfiltered fetch and the
  // sprint-filtered fetch race, and the slower one must not overwrite the newer.
  const boardReqSeq = useRef(0);

  const loadBoardData = useCallback(async () => {
    if (!projectId) return;
    const reqId = ++boardReqSeq.current;
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (effectiveSprintId) params.sprint_id = effectiveSprintId;
      if (showAllDone) params.show_all_done = true;
      const response = await tasksAPI.getBoardView(projectId, params);
      if (reqId !== boardReqSeq.current) return; // a newer request superseded this one
      setBoardData(response.data);
    } catch (err) {
      if (reqId !== boardReqSeq.current) return;
      setError('Fehler beim Laden des Boards');
      console.error('[PM] board load error', err);
    } finally {
      if (reqId === boardReqSeq.current) setLoading(false);
    }
  }, [projectId, effectiveSprintId, showAllDone]);

  useEffect(() => {
    loadBoardData();
  }, [loadBoardData]);

  const handleSprintChange = (value) => {
    setShowAllDone(false); // re-window the done column on every switch
    setSelectedSprintId(value);
  };

  useImperativeHandle(ref, () => ({ reload: loadBoardData }), [loadBoardData]);

  // --- Suche ---------------------------------------------------------------
  // Eigener Sequenzguard wie beim Board: beim Tippen überholen sich Antworten
  // sonst gegenseitig und ein älteres Ergebnis überschreibt das neuere.
  const searchReqSeq = useRef(0);

  useEffect(() => {
    const query = searchQuery.trim();
    if (!query) {
      searchReqSeq.current += 1; // laufende Antwort verwerfen
      setSearchResult(null);
      setSearchLoading(false);
      return;
    }

    const reqId = ++searchReqSeq.current;
    setSearchLoading(true);
    searchAPI
      .tasks({
        q: query,
        project_id: projectId,
        scope: searchAllProjects ? 'all' : 'project',
        include_archived: true,
      })
      .then((res) => {
        if (reqId !== searchReqSeq.current) return;
        setSearchResult(res.data);
      })
      .catch((err) => {
        if (reqId !== searchReqSeq.current) return;
        console.error('[PM] search error', err);
        setSearchResult({ results: [], terms: [], counts: { total: 0 } });
      })
      .finally(() => {
        if (reqId === searchReqSeq.current) setSearchLoading(false);
      });
  }, [searchQuery, searchAllProjects, projectId, searchNonce]);

  // Beim Projektwechsel darf kein Filter des Vorgängers hängen bleiben.
  useEffect(() => { setSearchQuery(''); setSearchAllProjects(false); }, [projectId]);

  const searchActive = Boolean(searchQuery.trim()) && Boolean(searchResult);
  const matchIds = useMemo(
    () => new Set((searchResult?.results || []).map((r) => r.task.id)),
    [searchResult],
  );

  // Welche Treffer zeigt das Board gerade — und welche nicht? Rein clientseitig
  // aus dem geladenen boardData, damit der Server dessen Done-Fenster- und
  // Sprint-Logik nicht nachbauen muss.
  const { onBoardCount, offBoardResults } = useMemo(() => {
    if (!searchActive) return { onBoardCount: 0, offBoardResults: [] };
    const rendered = new Set();
    (boardData?.board || []).forEach((col) => col.tasks.forEach((t) => rendered.add(t.id)));
    (boardData?.backlog || []).forEach((t) => rendered.add(t.id));

    const results = searchResult.results || [];
    return {
      onBoardCount: results.filter((r) => rendered.has(r.task.id)).length,
      offBoardResults: results.filter((r) => !rendered.has(r.task.id)),
    };
  }, [searchActive, searchResult, boardData]);

  const visibleTasks = useCallback(
    (tasks) => (searchActive ? tasks.filter((t) => matchIds.has(t.id)) : tasks),
    [searchActive, matchIds],
  );

  const searchSummary = () => {
    if (!searchActive) return '';
    const total = searchResult.counts?.total || 0;
    if (total === 0) return 'keine Treffer';
    const parts = [`${total} Treffer`];
    if (offBoardResults.length) parts.push(`${onBoardCount} auf diesem Board`);
    const comments = searchResult.counts?.comment_matches || 0;
    if (comments) parts.push(`${comments} Fundstelle${comments === 1 ? '' : 'n'} in Kommentaren`);
    return parts.join(' · ');
  };

  /** Treffer außerhalb des Boards im gewohnten Ticket-Dialog öffnen. */
  const openSearchResult = async (result) => {
    try {
      const res = await tasksAPI.getById(result.task.id);
      setEditTask(res.data);
      setEditDialogOpen(true);
    } catch (err) {
      console.error('[PM] open search result error', err);
      setError('Ticket konnte nicht geladen werden');
    }
  };

  // --- helpers (mirror BoardPage) ---
  const findTaskById = (id) => {
    if (!boardData) return null;
    for (const column of boardData.board) {
      const task = column.tasks.find((t) => t.id === id);
      if (task) return task;
    }
    return null;
  };
  const findBacklogTaskById = (id) =>
    boardData && boardData.backlog ? boardData.backlog.find((t) => t.id === id) : null;
  const findStatusByTaskId = (taskId) => {
    if (!boardData) return null;
    for (const column of boardData.board) {
      if (column.tasks.some((t) => t.id === taskId)) return column.status;
    }
    return null;
  };
  const findStatusById = (statusId) => {
    if (!boardData) return null;
    const column = boardData.board.find((col) => col.status.id === statusId);
    return column ? column.status : null;
  };
  const findTasksByStatusId = (statusId) => {
    if (!boardData) return [];
    const column = boardData.board.find((col) => col.status.id === statusId);
    return column ? column.tasks : [];
  };

  const moveTask = async (task, newStatusId, newPosition) => {
    try {
      await tasksAPI.move(task.id, newStatusId, newPosition);
      await loadBoardData();
      if (onTasksChanged) onTasksChanged();
    } catch (err) {
      setError('Fehler beim Verschieben der Aufgabe');
      console.error('[PM] move error', err);
    }
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const taskId = active.id.toString().startsWith('backlog-')
      ? parseInt(active.id.toString().replace('backlog-', ''))
      : active.id;
    setActiveTask(findTaskById(taskId) || findBacklogTaskById(taskId));
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over) { setActiveTask(null); return; }

    // Bei aktiver Suche zeigt jede Spalte nur die Treffer. Die Zielposition
    // würde dann aus einer Teilliste abgeleitet und falsch gespeichert –
    // deshalb ist Verschieben während der Suche gesperrt.
    if (searchActive) { setActiveTask(null); return; }

    const activeId = active.id;
    const overId = over.id;
    const isFromBacklog = activeId.toString().startsWith('backlog-');
    const taskId = isFromBacklog ? parseInt(activeId.toString().replace('backlog-', '')) : activeId;

    const task = isFromBacklog ? findBacklogTaskById(taskId) : findTaskById(taskId);
    const targetStatus = findStatusById(overId) || findStatusByTaskId(overId);

    if (!task || !targetStatus) { setActiveTask(null); return; }

    if (isFromBacklog) {
      // Backlog tasks can be promoted into any board column
      const targetTasks = findTasksByStatusId(targetStatus.id);
      const newPosition = overId === targetStatus.id ? targetTasks.length : targetTasks.findIndex((t) => t.id === overId);
      await moveTask(task, targetStatus.id, newPosition);
    } else if (task.status_id === targetStatus.id) {
      const statusTasks = findTasksByStatusId(targetStatus.id);
      const oldIndex = statusTasks.findIndex((t) => t.id === taskId);
      const newIndex = statusTasks.findIndex((t) => t.id === overId);
      if (oldIndex !== newIndex && newIndex !== -1) await moveTask(task, targetStatus.id, newIndex);
    } else {
      const targetTasks = findTasksByStatusId(targetStatus.id);
      const newPosition = overId === targetStatus.id ? targetTasks.length : targetTasks.findIndex((t) => t.id === overId);
      await moveTask(task, targetStatus.id, newPosition);
    }
    setActiveTask(null);
  };

  const handleTaskClick = (task) => { setEditTask(task); setEditDialogOpen(true); };
  const handleEditDialogClose = () => { setEditDialogOpen(false); setEditTask(null); };
  const handleTaskUpdated = () => {
    loadBoardData();
    if (searchQuery.trim()) setSearchNonce((n) => n + 1); // Treffer neu bewerten
    if (onTasksChanged) onTasksChanged();
  };

  const dropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({ styles: { active: { opacity: '0.3' } } }),
  };

  return (
    <Box>
      {/* Sprint switcher: pick a (parallel) sprint/area to focus, or show all */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Sprint / Bereich</InputLabel>
          <Select
            value={selectedSprintId}
            label="Sprint / Bereich"
            onChange={(e) => handleSprintChange(e.target.value)}
          >
            <MenuItem value="all">Alle anzeigen</MenuItem>
            {sprints.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedSprintId !== 'all' && (
          <Chip
            size="small" color="primary" variant="outlined"
            label={(sprints.find((s) => s.id === selectedSprintId) || {}).name || 'Sprint'}
          />
        )}
        {sprints.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            Noch keine Sprints – im Tab „Sprints" anlegen.
          </Typography>
        )}
      </Box>

      {/* Suche: filtert die Spalten in-place, Treffer außerhalb landen im Panel */}
      <Box sx={{ mb: 2 }}>
        <BoardSearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          allProjects={searchAllProjects}
          onAllProjectsChange={setSearchAllProjects}
          loading={searchLoading}
          summary={searchSummary()}
        />
        {searchActive && (
          <Chip
            size="small"
            variant="outlined"
            label="Sortieren während der Suche pausiert"
            sx={{ mt: 1 }}
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      )}

      {!loading && boardData && boardData.board && boardData.board.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2, height: '68vh', alignItems: 'stretch' }}>
            {boardData.board.map((column) => (
              <KanbanColumn
                key={column.status.id}
                status={column.status}
                tasks={visibleTasks(column.tasks)}
                totalCount={searchActive ? column.tasks.length : null}
                onTaskClick={handleTaskClick}
                hiddenDoneCount={searchActive ? 0 : (column.hidden_done_count || 0)}
                onShowAllDone={() => setShowAllDone(true)}
                sortableDisabled={searchActive}
              />
            ))}
          </Box>

          {boardData.backlog && boardData.backlog.length > 0
            && visibleTasks(boardData.backlog).length > 0 && (
            <BacklogTable tasks={visibleTasks(boardData.backlog)} onTaskClick={handleTaskClick} />
          )}

          <DragOverlay dropAnimation={dropAnimation}>
            {activeTask ? (
              <Box sx={{ transform: 'rotate(5deg)', opacity: 0.9, width: '300px', maxWidth: '300px', cursor: 'grabbing' }}>
                <TaskCard task={activeTask} isDragging />
              </Box>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Außerhalb des DndContext: das Panel muss auch dann erscheinen, wenn
          das Board leer ist – sonst wäre ein Treffer im Archiv unsichtbar. */}
      {searchActive && (
        <SearchResultsPanel
          offBoardResults={offBoardResults}
          terms={searchResult.terms || []}
          onOpen={openSearchResult}
          onBoardCount={onBoardCount}
        />
      )}

      {!loading && boardData && (!boardData.board || boardData.board.length === 0) && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Keine Tasks für dieses Projekt vorhanden
          </Typography>
        </Paper>
      )}

      <TaskEditDialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        task={editTask}
        projectId={projectId}
        onTaskUpdated={handleTaskUpdated}
      />
    </Box>
  );
});

export default ProjectBoard;
